export const sanitizeIpList = (ips) =>
  Array.from(
    new Set(
      (Array.isArray(ips) ? ips : [])
        .map((ip) => String(ip || "").trim())
        .filter(Boolean),
    ),
  );

export const normalizeWhitelistedRecord = (record = {}) => ({
  id: Number(record.id || 0) || null,
  client_id: Number(record.client_id || 0) || null,
  ip_address: String(record.ip_address || "").trim(),
  created_at: record.created_at || "",
  updated_at: record.updated_at || "",
});

export const normalizeWhitelistedRecords = (records = []) =>
  records
    .map(normalizeWhitelistedRecord)
    .filter((item) => item.ip_address)
    .filter(
      (item, index, list) =>
        list.findIndex((entry) => entry.ip_address === item.ip_address) === index,
    );

export const normalizeMultilineText = (value) => {
  const raw = String(value || "").trim();
  const stripped =
    (raw.startsWith('"') && raw.endsWith('"')) ||
    (raw.startsWith("'") && raw.endsWith("'"))
      ? raw.slice(1, -1)
      : raw;

  return stripped
    .replace(/\\r\\n/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/\\r/g, "\n")
    .replace(/\\t/g, "  ")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n");
};

export const resolveParserDelimiter = (value) => {
  const raw = String(value || "").trim();
  if (!raw) {
    return "-";
  }

  if (raw === ":" || raw === "-" || raw === "|" || raw === "=") {
    return raw;
  }

  const delimiterCandidates = [":", "-", "|", "="];
  const inferredDelimiter = delimiterCandidates.find((token) => raw.includes(token));
  if (inferredDelimiter) {
    return inferredDelimiter;
  }

  // If caller-id tag text is passed instead of a delimiter token, default to '-'.
  if (raw.length > 3 || /\s/.test(raw)) {
    return "-";
  }

  return raw;
};

export const parseEmailBodyLocally = (emailBody) => {
  const normalized = normalizeMultilineText(emailBody);
  const lines = normalized
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const matchValue = (labels) => {
    if (!labels.length) return "";

    const labelPattern = labels
      .map((label) => label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
      .join("|");
    const regex = new RegExp(`^(?:${labelPattern})\\s*[:\\-]\\s*(.+)$`, "i");

    for (const line of lines) {
      const match = line.match(regex);
      if (match?.[1]) {
        return match[1].trim();
      }
    }

    return "";
  };

  const caller = matchValue(["Caller", "Caller ID", "CallerID", "Caller Id", "From"]);
  const time = matchValue(["Time", "Received", "Date", "Date/Time", "Timestamp"]);
  const duration = matchValue(["Duration", "Length", "Message Length"]);

  return {
    caller,
    time,
    duration,
    normalized,
  };
};

export const extractParserData = (response) => {
  if (!response || response.error) {
    return null;
  }

  if (response?.parser_data && typeof response.parser_data === "object") {
    return response.parser_data;
  }

  if (response?.data?.parser_data && typeof response.data.parser_data === "object") {
    return response.data.parser_data;
  }

  if (
    response?.caller !== undefined ||
    response?.time !== undefined ||
    response?.duration !== undefined
  ) {
    return response;
  }

  return null;
};

export const hasParserValues = (parserData) => {
  if (!parserData || typeof parserData !== "object") {
    return false;
  }

  return ["caller", "time", "duration"].some((key) =>
    String(parserData?.[key] || "").trim(),
  );
};

export const formatParsedPreview = (parserData) => {
  const caller = String(parserData?.caller || "-");
  const duration = String(parserData?.duration || "-");
  const time = String(parserData?.time || "-");

  return `Caller ID: ${caller}\nDuration: ${duration}\nReceived: ${time}`;
};
